import type { ReactNode } from 'react';
import type { SectionState } from '../lib/site.js';
import { ComingSoon } from './ComingSoon.js';
import { Icons, StateCard } from './StateCard.js';

/**
 * The three-way branch every visibility-controlled section takes.
 *
 * This component exists so the branch is written once. It is the single rule
 * the whole visibility feature rests on (design §8, T9) and it is easy to get
 * subtly wrong — `sessions?.length ? … : <ComingSoon/>` reads fine and is
 * wrong, because it shows "coming soon" to an event that is published with
 * nothing scheduled yet.
 *
 *   key absent  → the organisers have hidden this  → the themed coming-soon card
 *   key present, []  → visible, nothing in it yet  → the section's own empty state
 *   key present, [x] → the real thing
 */
export function Section<T>({
  state,
  comingSoon,
  emptyTitle,
  emptyBody,
  children,
}: {
  state: SectionState<T>;
  comingSoon: { title: string; body: string };
  emptyTitle: string;
  emptyBody: string;
  children: (items: T[]) => ReactNode;
}) {
  if (state.kind === 'hidden') {
    return <ComingSoon title={comingSoon.title} body={comingSoon.body} />;
  }
  if (state.kind === 'empty') {
    return <StateCard icon={Icons.calendar} title={emptyTitle} body={emptyBody} tone="quiet" />;
  }
  return <>{children(state.items)}</>;
}
